import React from 'react';
import PropTypes from 'prop-types';
import util from 'util';
import { css, cx } from 'emotion';
import Link from 'gatsby-link';
import Color from 'color';

import { colors, baselineGrid, animation } from '../utils/presets';
import { options } from '../utils/typography';
import { toRem, fp, mqObj as mq } from '../utils/functions';

import ArrowBack from '../assets/svg-sprite/shape-arrow-back-large.svg';
import ArrowForward from '../assets/svg-sprite/shape-arrow-forward-large.svg';

const { speed, curve } = animation;

const spacing = fp({
  margin: [
    `${toRem(baselineGrid * 4)} auto`,
    `${toRem(baselineGrid * 4)} auto`,
    `${toRem(baselineGrid * 6)} auto`,
  ],
});

const pagination = css`
  ${spacing};
  display: flex;
  flex-direction: column;
  justify-content: center;
  font-family: ${options.headerFontFamily.join(',')};
  font-size: ${toRem(baselineGrid * 2.5)};
  color: ${colors.gray.gunpowder};

  ${mq.lap} {
    flex-direction: row;
  }
`;

const compact = css`
  flex-direction: row;
`;

const paginationCell = css`
  flex: 0 0 auto;
  padding: ${toRem(baselineGrid)} ${toRem(baselineGrid * 2)};
  text-align: center;
`;

const previous = css``;
const next = css``;
const previousLink = css``;
const nextLink = css``;

const paginationLink = css`
  text-decoration: none;
  color: inherit;

  .arrow {
    display: inline-block;
    vertical-align: middle;
    width: ${toRem(baselineGrid * 6)};
    height: ${toRem(baselineGrid * 4)};
    transform: translate(0, -2px);
    fill: ${Color(colors.accent.green)
      .hsl()
      .darken(0)
      .string()};
    transition: fill ${speed.default} ${curve.default};
  }

  &.disabled,
  &:disabled {
    opacity: 0.35;

    .arrow {
      fill: ${colors.gray.gunpowder};
    }
  }

  &:hover,
  &:active,
  &:focus {
    .arrow {
      fill: ${colors.gray.gunpowder};
    }
  }
`;

const counterSmall = css`
  padding-left: 0;
  padding-right: 0;
`;

const PaginationLink = props => {
  if (!props.test) {
    return (
      <Link className={cx(paginationLink, props.className)} to={props.url}>
        {props.children}
      </Link>
    );
  }
  return (
    <span className={cx(paginationLink, props.className, 'disabled')} disabled>
      {props.children}
    </span>
  );
};

PaginationLink.propTypes = {
  test: PropTypes.bool,
  url: PropTypes.string,
  text: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

const Pagination = ({
  isFirstPage,
  isLastPage,
  previousUrl,
  nextUrl,
  currentPage,
  totalPages,
  countFormat,
  previousLinkText,
  nextLinkText,
  isCompact,
  arrows,
}) => (
  <nav className={cx(pagination, { [compact]: isCompact })}>
    <div className={cx(paginationCell, previous)}>
      <PaginationLink test={isFirstPage} url={previousUrl} className={previousLink}>
        {arrows && (
          <svg className={'arrow'} viewBox={ArrowBack.viewBox}>
            <use xlinkHref={`#${ArrowBack.id}`} />
          </svg>
        )}
        <span>{previousLinkText}</span>
      </PaginationLink>
    </div>
    <div className={cx(paginationCell, { [counterSmall]: isCompact })}>
      <span>{util.format(countFormat, currentPage, totalPages)}</span>
    </div>
    <div className={cx(paginationCell, next)}>
      <PaginationLink test={isLastPage} url={nextUrl} className={nextLink} text={nextLinkText}>
        <span>{nextLinkText}</span>
        {arrows && (
          <svg className={'arrow'} viewBox={ArrowForward.viewBox}>
            <use xlinkHref={`#${ArrowForward.id}`} />
          </svg>
        )}
      </PaginationLink>
    </div>
  </nav>
);

Pagination.propTypes = {
  isFirstPage: PropTypes.bool,
  isLastPage: PropTypes.bool,
  previousUrl: PropTypes.string,
  nextUrl: PropTypes.string,
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  countFormat: PropTypes.string,
  previousLinkText: PropTypes.string,
  nextLinkText: PropTypes.string,
  isCompact: PropTypes.bool,
  arrows: PropTypes.bool,
};

Pagination.defaultProps = {
  countFormat: 'Page %s of %s',
  previousLinkText: 'Previous Page',
  nextLinkText: 'Next Page',
  isCompact: false,
  arrows: true,
};

export default Pagination;



// WEBPACK FOOTER //
// ./src/components/pagination.js